import { useState } from 'react';

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { InsightFilter, InsightOperator } from '@/shared/types/analytics';
import { Insight } from '@/shared/types/insights';

import { DEFAULT_OPERATORS, OPERATOR_LABELS } from './constants';

interface DateTimeInsightFilterProps {
  filterIndex: number;
  insight: Insight;
}

export const DateTimeInsightFilter = ({ filterIndex, insight }: DateTimeInsightFilterProps) => {
  const { control, getValues } = useFormContext();
  const { t } = useTranslation();
  const { update } = useFieldArray({ control, name: 'filters' });
  const [open, setOpen] = useState(false);

  // Get the insightFilters array
  const insightFilters = useWatch({ control, name: `filters.${filterIndex}.insightFilters` }) || [];

  // Check if this insight already exists in the filters
  const existingFilterIndex = insightFilters.findIndex(
    (filter: InsightFilter) => filter.id === insight.name
  );

  const currentFilter =
    existingFilterIndex !== -1
      ? insightFilters[existingFilterIndex]
      : {
          id: insight.name,
          category: insight.type,
          operator: DEFAULT_OPERATORS[insight.type] || 'eq',
          values: [],
        };

  const updateFilter = (newInsightFilter: InsightFilter) => {
    const updatedFilters = [...insightFilters];

    if (existingFilterIndex !== -1) {
      // Update existing filter
      updatedFilters[existingFilterIndex] = newInsightFilter;
    } else {
      // Add new filter
      updatedFilters.push(newInsightFilter);
    }

    const currentFormFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFormFilter, insightFilters: updatedFilters });
  };

  const handleOperatorChange = (newOperator: InsightOperator) => {
    // Reset values when operator changes - always empty for 'exists' operator
    updateFilter({ ...currentFilter, operator: newOperator, values: [] });
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      const isoDate = format(date, 'yyyy-MM-dd');
      updateFilter({ ...currentFilter, values: [isoDate] });
      setOpen(false); // Close the popover after selecting a date
    } else {
      // Clear the filter if date is undefined
      updateFilter({ ...currentFilter, values: [] });
    }
  };

  const handleRangeSelect = (range: { from?: Date; to?: Date } | undefined) => {
    if (range?.from && range?.to) {
      const fromDate = format(range.from, 'yyyy-MM-dd');
      const toDate = format(range.to, 'yyyy-MM-dd');
      updateFilter({ ...currentFilter, values: [fromDate, toDate] });
    } else if (range?.from && !range?.to) {
      // Handle partial selection - store the first date temporarily
      const fromDate = format(range.from, 'yyyy-MM-dd');
      updateFilter({ ...currentFilter, values: [fromDate] });
    } else {
      // Clear the filter if range is undefined or incomplete
      updateFilter({ ...currentFilter, values: [] });
    }
  };

  const operatorOptions = OPERATOR_LABELS[insight.type] || {};
  const isBetweenOperator = currentFilter.operator === 'between';
  const isExistsOperator = currentFilter.operator === 'exists';

  // Parse existing dates - handle both date-only and datetime formats
  const selectedDate = (() => {
    if (currentFilter.values[0]) {
      try {
        // Parse the date string - works with both "yyyy-MM-dd" and "yyyy-MM-dd'T'HH:mm:ss"
        const date = new Date(currentFilter.values[0]);
        return isNaN(date.getTime()) ? undefined : date;
      } catch {
        return undefined;
      }
    }
    return undefined;
  })();

  const selectedRange = (() => {
    if (isBetweenOperator && currentFilter.values.length >= 1) {
      try {
        const from = new Date(currentFilter.values[0]);
        const to = currentFilter.values[1] ? new Date(currentFilter.values[1]) : undefined;

        if (!isNaN(from.getTime())) {
          return { from, to: to && !isNaN(to.getTime()) ? to : undefined };
        }
      } catch {
        return undefined;
      }
    }
    return undefined;
  })();

  const formatDateDisplay = () => {
    if (isBetweenOperator && currentFilter.values.length === 2) {
      try {
        return `${format(new Date(currentFilter.values[0]), 'MMM d, yyyy')} - ${format(
          new Date(currentFilter.values[1]),
          'MMM d, yyyy'
        )}`;
      } catch {
        return t('filters.placeholders.select_date');
      }
    } else if (currentFilter.values[0]) {
      try {
        return format(new Date(currentFilter.values[0]), 'PPP');
      } catch {
        return t('filters.placeholders.select_date');
      }
    }
    return t('filters.placeholders.select_date');
  };

  return (
    <div className="space-y-3">
      <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
        <Select value={currentFilter.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(operatorOptions).map(([op, label]) => (
              <SelectItem key={op} value={op}>
                {t(label as string)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!isExistsOperator ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline-neutral"
              className={cn(
                'w-full justify-start text-left font-normal',
                !currentFilter.values.length && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">{formatDateDisplay()}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isBetweenOperator ? (
              <Calendar
                mode="range"
                defaultMonth={selectedRange?.from}
                selected={selectedRange}
                onSelect={handleRangeSelect}
                numberOfMonths={2}
                className="rounded-md border"
              />
            ) : (
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                autoFocus
                className="rounded-md border"
              />
            )}
          </PopoverContent>
        </Popover>
      ) : (
        <div className="rounded-md border border-border bg-subtle p-2 text-sm text-muted-foreground">
          {t('filters.exists_description', 'Any non-null value')}
        </div>
      )}
    </div>
  );
};
